// Shared navigation registry — the single source of truth for the reader
// navbar's primary links.
//
// Pure data, no React / server-only imports, so it can be consumed by BOTH the
// UI (reader-navbar.tsx maps over it) AND the assistant feature map
// (src/lib/ai/feature-map.ts derives "where things live" answers from it).
// Because both read the same array, a rename here updates the button AND what
// the AI tells authors — they cannot drift out of sync.

type NavAudience = "reader" | "author" | "both";

export type NavDestination = {
    /** Stable key — also used by the feature map to attach AI aliases. */
    id: string;
    label: string;
    /**
     * Route. May contain `:storyId` / `:wikiId` placeholders (editor items).
     * Empty for not-yet-shipped ("Soon") items.
     */
    href?: string;
    /** Active-page key the navbar compares against its current page. */
    page?: string;
    audience: NavAudience;
    /** True for "coming soon" placeholders — rendered disabled, never a real destination. */
    disabled?: boolean;
    /** Editor item that only renders/resolves once the story has a world. */
    requiresWorldId?: boolean;
};

export const READER_NAV_ITEMS: NavDestination[] = [
    { id: "browse", label: "Browse", href: "/browse", page: "browse", audience: "both" },
    { id: "library", label: "Library", href: "/library", page: "library", audience: "reader" },
    { id: "history", label: "History", href: "/history", page: "history", audience: "reader" },
    { id: "fan-clubs", label: "Fan Clubs", href: "/fan-clubs", page: "fan-clubs", audience: "reader" },
    { id: "community", label: "Community", page: undefined, audience: "reader", disabled: true },
];

// Editor (author IDE) sub-navbar links for a single story. Routes carry
// `:storyId` / `:wikiId` placeholders that the consumer resolves. Consumed by
// story-sub-navbar.tsx (renders + active state) and the feature map (so the
// assistant names these the same way the UI does).
export const EDITOR_NAV_ITEMS: NavDestination[] = [
    { id: "dashboard", label: "Dashboard", href: "/editor/:storyId/dashboard", audience: "author" },
    { id: "editor", label: "Editor", href: "/editor/:storyId", audience: "author" },
    {
        id: "wiki",
        label: "Wiki",
        href: "/wiki/:wikiId",
        requiresWorldId: true,
        audience: "author",
    },
    { id: "plot-threads", label: "Plot Threads", href: "/editor/:storyId/plot-threads", audience: "author" },
    { id: "paywall", label: "Controls", href: "/editor/:storyId/paywall", audience: "author" },
    { id: "analytics", label: "Analytics", href: "/editor/:storyId/analytics", audience: "author" },
    { id: "fan-club", label: "Fan Club", href: "/editor/:storyId/fan-club", audience: "author" },
];

/** Resolve `:storyId` / `:wikiId` placeholders in a nav href for rendering. */
export function resolveNavHref(
    href: string,
    params: { storyId?: string; wikiId?: string }
): string {
    return href
        .replaceAll(":storyId", params.storyId ?? "")
        .replaceAll(":wikiId", params.wikiId ?? "");
}
